import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

const MARGIN = { top: 20, right: 20, bottom: 50, left: 50 };
const SVG_WIDTH = 800;
const SVG_HEIGHT = 350;

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  header: {
    marginBottom: '16px',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    margin: 0,
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    margin: '4px 0 0 0',
  },
  container: {
    overflowX: 'auto',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    marginTop: '16px',
    justifyContent: 'center',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
    color: '#374151',
  },
  legendSwatch: {
    width: '14px',
    height: '14px',
    borderRadius: '2px',
  },
  emptyState: {
    color: '#6b7280',
    textAlign: 'center',
    padding: '40px 0',
  },
};

export default function MarimekkoChart({ data }) {
  const svgRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    if (!data || !data.states || data.states.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const innerW = SVG_WIDTH - MARGIN.left - MARGIN.right;
    const innerH = SVG_HEIGHT - MARGIN.top - MARGIN.bottom;

    svg
      .attr('viewBox', `0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`)
      .attr('width', '100%')
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .style('max-height', `${SVG_HEIGHT}px`);

    const g = svg
      .append('g')
      .attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

    const totalAddresses = data.totalAddresses || d3.sum(data.states, (s) => s.addressCount);
    const gap = 1.5;
    const totalGap = gap * (data.states.length - 1);
    const usableWidth = innerW - totalGap;

    // Y axis: percentage scale 0-100%
    const y = d3.scaleLinear().domain([0, 100]).range([innerH, 0]);

    // Horizontal grid lines
    const yTicks = y.ticks(5);
    g.selectAll('.grid-line')
      .data(yTicks)
      .enter()
      .append('line')
      .attr('class', 'grid-line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', (d) => y(d))
      .attr('y2', (d) => y(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // Y axis
    g.append('g')
      .call(
        d3
          .axisLeft(y)
          .ticks(5)
          .tickSize(0)
          .tickFormat((d) => `${d}%`)
      )
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    // Draw columns
    let xOffset = 0;

    data.states.forEach((stateData) => {
      const colWidth = (stateData.addressCount / totalAddresses) * usableWidth;
      const totalCount = stateData.addressCount;

      // Stack tier segments from bottom to top
      let yOffset = 0;

      stateData.tiers.forEach((tier) => {
        const pct = totalCount > 0 ? (tier.count / totalCount) * 100 : 0;
        const segHeight = (pct / 100) * innerH;

        if (segHeight <= 0) return;

        const segY = innerH - yOffset - segHeight;

        g.append('rect')
          .attr('x', xOffset)
          .attr('y', segY)
          .attr('width', Math.max(0, colWidth))
          .attr('height', segHeight)
          .attr('fill', tier.color)
          .attr('stroke', '#fff')
          .attr('stroke-width', 0.5)
          .style('cursor', 'pointer')
          .on('mousemove', (event) => {
            d3.select(event.currentTarget).attr('opacity', 0.8);
            setTooltip({
              x: event.clientX + 12,
              y: event.clientY - 10,
              state: stateData.state,
              addressCount: stateData.addressCount,
              tiers: stateData.tiers,
              hoveredTier: tier.tier,
            });
          })
          .on('mouseleave', (event) => {
            d3.select(event.currentTarget).attr('opacity', 1);
            setTooltip(null);
          });

        yOffset += segHeight;
      });

      // State label on x-axis
      const labelX = xOffset + colWidth / 2;
      if (colWidth > 18) {
        g.append('text')
          .attr('x', labelX)
          .attr('y', innerH + 16)
          .attr('text-anchor', 'middle')
          .attr('font-size', colWidth > 30 ? '10px' : '8px')
          .attr('fill', '#6b7280')
          .text(stateData.state);

        // Address count below state label
        g.append('text')
          .attr('x', labelX)
          .attr('y', innerH + 28)
          .attr('text-anchor', 'middle')
          .attr('font-size', '8px')
          .attr('fill', '#9ca3af')
          .text(`(${stateData.addressCount})`);
      }

      xOffset += colWidth + gap;
    });

    // Bottom axis line
    g.append('line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', innerH)
      .attr('y2', innerH)
      .attr('stroke', '#e5e7eb')
      .attr('stroke-width', 1);

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data]);

  if (!data || !data.states || data.states.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.title}>Market Composition</div>
          <div style={styles.subtitle}>
            State market size (width) × price tier distribution (height)
          </div>
        </div>
        <div style={styles.emptyState}>No market composition data available</div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div style={styles.title}>Market Composition</div>
        <div style={styles.subtitle}>
          State market size (width) × price tier distribution (height)
        </div>
      </div>
      <div style={styles.container}>
        <svg ref={svgRef} />
      </div>
      <div style={styles.legend}>
        {(data.tierLabels || []).map((label, i) => (
          <div key={label} style={styles.legendItem}>
            <span
              style={{
                ...styles.legendSwatch,
                background: (data.tierColors || [])[i] || '#6b7280',
              }}
            />
            <span>{label}</span>
          </div>
        ))}
      </div>
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {tooltip.state}
          </div>
          <div
            style={{
              fontSize: '11px',
              color: '#9ca3af',
              marginBottom: '6px',
            }}
          >
            {tooltip.addressCount} address{tooltip.addressCount !== 1 ? 'es' : ''}
          </div>
          {tooltip.tiers.map((tier) => (
            <div
              key={tier.tier}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: '16px',
                marginBottom: '2px',
                fontWeight: tier.tier === tooltip.hoveredTier ? 600 : 400,
              }}
            >
              <span style={{ color: tier.color }}>{tier.tier}</span>
              <span style={{ fontWeight: 600 }}>
                {tier.count} ({tooltip.addressCount > 0
                  ? ((tier.count / tooltip.addressCount) * 100).toFixed(0)
                  : 0}%)
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
